// Tile model (GDD-compatible) + two-type run analysis
//
// Tile shape:
// {
//   id: string,
//   coord: { q: number, r: number },
//   rotation: 0|1|2|3|4|5,
//   sides: [TileType x6],
//   placed: boolean,
//   placedAtMs?: number,
//   seed?: number,
//   baseSpriteKey?: string,       // atlas key chosen at placement
//   transitionSpriteKey?: string, // atlas key for minority transition overlay
//   overlaySpriteKey?: string,    // atlas key for placed overlay decoration
// }

import { TILE_TYPES } from './tileTypes.mjs';

/**
 * Analyse the contiguous two-type run in a tile's sides array.
 * Returns { a, b, aCount, startIndex } where:
 *   a = primary (majority or first) type
 *   b = secondary type (null if mono)
 *   aCount = number of sides of type a
 *   startIndex = index where the a-run begins
 *
 * If the sides are invalid (non-contiguous or >2 types), returns a fallback
 * with .invalid = true.
 */
export function analyseTwoTypeRun(sides) {
  const types = [...new Set(sides)];

  if (types.length === 1) {
    return { a: types[0], b: null, aCount: 6, startIndex: 0, invalid: false };
  }

  if (types.length > 2) {
    return { a: types[0], b: types[1], aCount: 3, startIndex: 0, invalid: true };
  }

  const typeA = types[0];
  const typeB = types[1];
  const countA = sides.filter(s => s === typeA).length;
  const countB = 6 - countA;

  // Find the start of the A-run (wrapping)
  // A contiguous pattern means A occupies a contiguous arc and B occupies the rest.
  // Check contiguity of typeA
  const aContiguous = isContiguous(sides, typeA);
  const bContiguous = isContiguous(sides, typeB);

  if (!aContiguous || !bContiguous) {
    return { a: typeA, b: typeB, aCount: countA, startIndex: 0, invalid: true };
  }

  // Determine primary = majority type
  let primary, secondary, primaryCount;
  if (countA >= countB) {
    primary = typeA;
    secondary = typeB;
    primaryCount = countA;
  } else {
    primary = typeB;
    secondary = typeA;
    primaryCount = countB;
  }

  // Find start index of the primary run
  const startIndex = findRunStart(sides, primary);

  return {
    a: primary,
    b: secondary,
    aCount: primaryCount,
    startIndex,
    invalid: false,
  };
}

function isContiguous(sides, type) {
  const count = sides.filter(s => s === type).length;
  if (count === 0 || count === 6) return true;
  // Count transitions around the ring; contiguous = exactly 2 (one enter, one exit)
  let transitions = 0;
  for (let i = 0; i < 6; i++) {
    if ((sides[i] === type) !== (sides[(i + 1) % 6] === type)) transitions++;
  }
  return transitions === 2;
}

function findRunStart(sides, type) {
  // Find the index where a contiguous run of `type` starts (wrapping considered)
  // Walk backwards from first occurrence until it's no longer that type
  let idx = sides.indexOf(type);
  while (sides[((idx - 1) % 6 + 6) % 6] === type) {
    idx = ((idx - 1) % 6 + 6) % 6;
    if (idx === sides.indexOf(type)) break; // full circle (mono)
  }
  return idx;
}

/**
 * Generate a tile following the new contiguous two-type rule.
 * @param {number} id - numeric id counter
 * @param {object} opts
 * @param {boolean} [opts.allowMono] - allow all-6-same tiles
 * @param {Set<string>|null} [opts.exposedTypes] - if provided, at least one of the
 *   tile's types must be in this set so a connection is always possible.
 * @returns Tile object (unplaced)
 */
export function generateContiguousTile(id, opts = {}) {
  const { allowMono = false, exposedTypes = null } = opts;

  let typeA, typeB;

  if (exposedTypes && exposedTypes.size > 0) {
    const exposed = [...exposedTypes];
    // At least one type must come from exposed types
    typeA = exposed[Math.floor(Math.random() * exposed.length)];
    typeB = TILE_TYPES[Math.floor(Math.random() * TILE_TYPES.length)];
    // Ensure typeB differs from typeA
    while (typeB === typeA) {
      typeB = TILE_TYPES[Math.floor(Math.random() * TILE_TYPES.length)];
    }
    // Randomly swap so the exposed type isn't always the majority
    if (Math.random() < 0.5) {
      const tmp = typeA;
      typeA = typeB;
      typeB = tmp;
    }
  } else {
    typeA = TILE_TYPES[Math.floor(Math.random() * TILE_TYPES.length)];
    typeB = TILE_TYPES[Math.floor(Math.random() * TILE_TYPES.length)];
    while (typeB === typeA) {
      typeB = TILE_TYPES[Math.floor(Math.random() * TILE_TYPES.length)];
    }
  }

  // Choose split: 1-5 for two-type, 6 only if allowMono
  const maxK = allowMono ? 6 : 5;
  const k = 1 + Math.floor(Math.random() * maxK);

  const sides = [];
  for (let i = 0; i < 6; i++) {
    sides.push(i < k ? typeA : typeB);
  }

  // If mono (k===6), sides is all typeA already
  if (k === 6) {
    // no typeB needed
  }

  // Apply a random rotation offset so the run doesn't always start at 0
  const offset = Math.floor(Math.random() * 6);
  const rotatedSides = [];
  for (let i = 0; i < 6; i++) {
    rotatedSides.push(sides[((i - offset) % 6 + 6) % 6]);
  }

  return {
    id: 'tile_' + id,
    coord: null,
    rotation: 0,
    sides: rotatedSides,
    placed: false,
    placedAtMs: null,
    seed: Math.floor(Math.random() * 0x7fffffff),
  };
}
